import React, { createRef, useCallback, useEffect, useState } from "react";
import ReactMapGL, { Marker, NavigationControl, FlyToInterpolator } from "react-map-gl";

import { getMapBounds } from "../../utils/map";
import PolylineOverlay from "./PolylineOverlay";
import useRefDimensions from "../../hooks/useRefDimensions";

import "mapbox-gl/dist/mapbox-gl.css";
import "./DefaultMapbox.scss";

const MAP_CONFIG = {
  zoom: 11,
  mapStyle: "mapbox://styles/theprof/cl8nwpgtl004z15mi7tjvhoaf",
  mapboxApiAccessToken: "pk.eyJ1IjoidGhlcHJvZiIsImEiOiJjazRxaTFpMDUwMXd6M21uMHRvYXBlanY1In0.7v5Itqbe2GQvWxtkefhcug",
  longitude: -122.41,
  latitude: 37.78,
};

const navStyle = {
  top: 0,
  left: 0,
  padding: "10px",
};

const DefaultMapbox = ({ markers, polylines, moveToPoint, defaultCenter = undefined, className, ...props }) => {
  const mapRef = React.useRef();
  const divRef = createRef();
  const dimensions = useRefDimensions(divRef);
  const [fitBounded, setFitBounded] = useState(false);

  const [viewport, setViewport] = useState({
    width: "100%",
    height: "100%",
  });

  useEffect(() => {
    if (!fitBounded && markers?.length && dimensions.width) {
      setFitBounded(true);
      const bounds = getMapBounds(viewport, markers, dimensions.width, dimensions.height);
      setViewport(pre => {
        return {
          ...pre,
          ...bounds,
        };
      });
    }
    // eslint-disable-next-line
  }, [fitBounded, dimensions, markers]);

  useEffect(() => {
    if (typeof moveToPoint !== "undefined") {
      props.resetMoveToPoint && props.resetMoveToPoint();
      setViewport(pre => {
        return {
          ...pre,
          longitude: moveToPoint.lng,
          latitude: moveToPoint.lat,
          transitionDuration: 500,
          transitionInterpolator: new FlyToInterpolator(),
        };
      });
    }
    // eslint-disable-next-line
  }, [moveToPoint]);

  const handleViewportChange = useCallback(newViewport => setViewport(newViewport), []);

  const showMarkers = React.useMemo(
    () =>
      markers.map((marker, index) => (
        <Marker
          key={index}
          longitude={marker.longitude}
          latitude={marker.latitude}
          offsetLeft={marker.offsetLeft}
          offsetTop={marker.offsetTop}
          className={marker.className}>
          {marker.content}
        </Marker>
      )),
    [markers],
  );

  const showPolylines = React.useMemo(
    () =>
      polylines &&
      polylines.map((polyline, index) => (
        <PolylineOverlay
          key={index}
          points={polyline.points}
          color={polyline.strokeColor}
          lineWidth={polyline.strokeWeight}
          opacity={polyline.strokeOpacity}
        />
      )),
    [polylines],
  );

  return (
    <div
      ref={divRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
      className={`DefaultMapbox ${className}`}>
      <ReactMapGL
        ref={mapRef}
        {...MAP_CONFIG}
        latitude={defaultCenter?.lat || MAP_CONFIG.latitude}
        longitude={defaultCenter?.lng || MAP_CONFIG.longitude}
        {...viewport}
        width="100%"
        height="100%"
        onViewportChange={handleViewportChange}>
        {showPolylines}
        {showMarkers}
        {props.children}
        <NavigationControl style={navStyle} />
      </ReactMapGL>
    </div>
  );
};

export default DefaultMapbox;
